import { t, Trans } from "@lingui/macro";
import { Stack, Typography } from "@mui/material";
import {
  MenuList,
  MenuListItemVariant,
  SystmAppIcon,
  WahooAppIcon,
  type DrawerState,
} from "@WahooFitness/redesignr";
import type { PropsWithChildren, ReactElement, ReactNode } from "react";
import AppStoreIconSvg from "./AppStoreIconSvg";
import GooglePlayIconSvg from "./GooglePlayIconSvg";

export const APPLE_MANAGE_URL = "https://account.apple.com/account/manage/section/subscriptions";
export const GOOGLE_MANAGE_URL = "https://play.google.com/store/account/subscriptions";

export const ICON_SIZE = 60;
export const CARD_ICON_SIZE = 44;

type DrawerParams = PropsWithChildren<Partial<DrawerState>>;

type ManagementDrawerConfig = {
  url: string;
  subscriptionLabel: string;
  headerIcon: ReactNode;
  cardIcon: ReactElement;
  title: ReactNode;
  description: ReactNode;
};

function buildManagementDrawer({
  url,
  subscriptionLabel,
  headerIcon,
  cardIcon,
  title,
  description,
}: ManagementDrawerConfig): DrawerParams {
  return {
    open: true,
    hidePuller: true,
    hideDivider: true,
    title: undefined,
    children: (
      <Stack alignItems="center" gap={3} px={2} pb={3} pt={1}>
        {headerIcon}
        <Stack gap={1.5} alignItems="center">
          <Typography variant="prose-lg-bold" textAlign="center">
            {title}
          </Typography>
          <Typography variant="prose-base" color="text.secondary" textAlign="center">
            {description}
          </Typography>
        </Stack>
        <MenuList
          variant="outlined"
          width="100%"
          listItems={[
            {
              id: "manage-subscription",
              content: t`Current subscription`,
              secondaryContent: subscriptionLabel,
              variant: MenuListItemVariant.ExternalLink,
              linkLocation: url,
              icon: cardIcon,
            },
          ]}
        />
      </Stack>
    ),
  };
}

type StoreDrawerParams = {
  url: string;
  subscriptionLabel: string;
  activeTierText: string;
  trialing?: boolean;
};

export function buildAppleManagementDrawer({
  url,
  subscriptionLabel,
  activeTierText,
}: StoreDrawerParams): DrawerParams {
  return buildManagementDrawer({
    url,
    subscriptionLabel,
    headerIcon: <AppStoreIconSvg size={ICON_SIZE} />,
    cardIcon: <AppStoreIconSvg size={CARD_ICON_SIZE} />,
    title: <Trans>Manage your subscription on Apple</Trans>,
    description: (
      <Trans>
        You&apos;re subscribed to Wahoo {activeTierText} through the App Store. To upgrade or change
        your plan, you&apos;ll need to manage it from your iPhone or iPad.
      </Trans>
    ),
  });
}

export function buildGoogleManagementDrawer({
  url,
  subscriptionLabel,
  activeTierText,
}: StoreDrawerParams): DrawerParams {
  return buildManagementDrawer({
    url,
    subscriptionLabel,
    headerIcon: <GooglePlayIconSvg size={ICON_SIZE} />,
    cardIcon: <GooglePlayIconSvg size={CARD_ICON_SIZE} />,
    title: <Trans>Manage your subscription in the Google Play Store</Trans>,
    description: (
      <Trans>
        You&apos;re subscribed to Wahoo {activeTierText} through the Play Store. To upgrade or
        change your plan, you&apos;ll need to manage it from your Android device.
      </Trans>
    ),
  });
}

export function buildSystmManagementDrawer({
  url,
  subscriptionLabel,
  activeTierText,
}: StoreDrawerParams): DrawerParams {
  return buildManagementDrawer({
    url,
    subscriptionLabel,
    headerIcon: (
      <SystmAppIcon width={ICON_SIZE} height={ICON_SIZE} style={{ borderRadius: 13.4 }} />
    ),
    cardIcon: (
      <SystmAppIcon width={CARD_ICON_SIZE} height={CARD_ICON_SIZE} style={{ borderRadius: 8 }} />
    ),
    title: <Trans>Manage your subscription in SYSTM</Trans>,
    description: (
      <Trans>
        You&apos;re subscribed to Wahoo {activeTierText} through SYSTM. To upgrade or change your
        plan, you&apos;ll need to manage it from the SYSTM app.
      </Trans>
    ),
  });
}

export function buildWahooManagementDrawer({
  url,
  subscriptionLabel,
  activeTierText,
  trialing,
}: StoreDrawerParams): DrawerParams {
  return buildManagementDrawer({
    url,
    subscriptionLabel,
    headerIcon: (
      <WahooAppIcon width={ICON_SIZE} height={ICON_SIZE} style={{ borderRadius: 13.4 }} />
    ),
    cardIcon: (
      <WahooAppIcon width={CARD_ICON_SIZE} height={CARD_ICON_SIZE} style={{ borderRadius: 10 }} />
    ),
    title: <Trans>Manage your subscription at Wahoofitness.com</Trans>,
    description: trialing ? (
      <Trans>
        You are on a Pro trial directly through wahoofitness.com. To manage your plan, sign into
        your account on the web.
      </Trans>
    ) : (
      <Trans>
        You are subscribed to Wahoo {activeTierText} directly through wahoofitness.com. To upgrade
        or change your plan, sign into your account on the web.
      </Trans>
    ),
  });
}
